/**
 * What starts a workflow. The list IS the event catalog.
 *
 * It used to be assembled by counting whatever had arrived in a 200-event
 * window, with a text box at the bottom that minted a brand-new event type on
 * the spot. That box promised "you'll get a reference to pass on" and then
 * handed over a slug; nothing checked that anything would ever send it, and
 * nothing decided who could build on it. Both problems belong to the Events
 * page, where a type's name, its scopes and its payload get settled once.
 *
 * So: the tenant's registered events lead, the platform's follow, and an
 * unlisted event is a link to go register one. A type absent because the
 * author lacks its scopes is filtered out server-side — which is why "not
 * listed" says both reasons rather than assuming the first.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Popover } from '@servicecompressionllc/sc-design-system';
import {
  SCHEDULE_TRIGGER,
  isScheduleTrigger,
  type BuilderSchedule,
  type WorkflowTrigger,
} from '../../lib/types.js';
import { DEFAULT_SCHEDULE, ScheduleEditor } from './ScheduleEditor.js';
import { humanizeKey } from './definitionEdit.js';

const seen = (n: number): string => (n > 0 ? `${String(n)} in the last 7 days` : 'none yet');

export const TriggerPicker = ({
  value,
  onChange,
  triggers,
  schedule,
  onScheduleChange,
  isNew = true,
}: {
  isNew?: boolean;
  value: string;
  onChange: (trigger: string) => void;
  /** The catalog, as the overview serves it: the tenant's approved types this
   *  author may build on, plus the platform's own. */
  triggers: WorkflowTrigger[];
  /** Set when this workflow runs on a schedule rather than on an event. */
  schedule: BuilderSchedule | null;
  onScheduleChange: (next: BuilderSchedule) => void;
}) => {
  const [open, setOpen] = useState(false);
  const mine = triggers.filter((t) => t.source === 'tenant');
  const platform = triggers.filter((t) => t.source === 'platform');

  const scheduled = isScheduleTrigger(value);
  const chosen = triggers.find((t) => t.type === value);
  // A workflow can point at a type that has since been unregistered, or one
  // whose scopes this author does not hold. Saying so beats rendering a blank.
  const label = scheduled
    ? 'On a schedule'
    : chosen
      ? chosen.title
      : value
        ? humanizeKey(value)
        : 'Choose what starts it';

  const pick = (type: string) => {
    setOpen(false);
    onChange(type);
  };

  const row = (t: WorkflowTrigger) => (
    <Popover.Item
      key={t.type}
      hint={t.hint ? `${t.hint} · ${seen(t.eventsLast7d)}` : seen(t.eventsLast7d)}
      onClick={() => pick(t.type)}
    >
      {t.title}
    </Popover.Item>
  );

  return (
    <div className="grid min-w-0 gap-2">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-ink-muted">Starts when</span>
        {!isNew && scheduled ? (
          <span aria-label="What starts this workflow" className="text-ink">
            {label}
          </span>
        ) : (
          <Popover open={open} onOpenChange={setOpen}>
            <Popover.Trigger
              aria-label="What starts this workflow"
              className={`flex items-center gap-2 rounded border px-3 py-1.5 text-sm ${
                value
                  ? 'border-outline-strong bg-surface text-ink'
                  : 'border-primary bg-surface text-ink-subtle'
              }`}
            >
              {label}
              <span aria-hidden className="text-xs text-ink-subtle">
                ▾
              </span>
            </Popover.Trigger>
            <Popover.Content
              aria-label="What can start a workflow"
              className="w-[26rem] max-w-[90vw]"
            >
              <ul className="pb-1">
                {isNew && (
                  <>
                    <Popover.Section>On a clock</Popover.Section>
                    <Popover.Item
                      hint="Runs on the days and at the time you choose, whether or not anything happened."
                      onClick={() => {
                        setOpen(false);
                        onChange(scheduled ? value : SCHEDULE_TRIGGER);
                        if (!schedule) onScheduleChange(DEFAULT_SCHEDULE);
                      }}
                    >
                      On a schedule
                    </Popover.Item>
                  </>
                )}
                {mine.length > 0 && <Popover.Section>This tenant’s events</Popover.Section>}
                {mine.map(row)}
                {platform.length > 0 && <Popover.Section>From the platform</Popover.Section>}
                {platform.map(row)}
                <Popover.Section tone="neutral">Not listed</Popover.Section>
                <li className="px-1">
                  <Link
                    // The Events page lives at /events (NOT under /settings) and reads
                    // ?tab= from the URL — a wrong prefix here sent people to a
                    // blank page at the exact moment they were promised help.
                    to="/events?tab=catalog"
                    className="block rounded px-2 py-1.5 hover:bg-surface-hover"
                  >
                    <span className="block text-sm font-medium text-info">
                      Register a new event
                    </span>
                    <span className="mt-0.5 block text-xs text-ink-muted">
                      For something no system sends yet — or something already arriving that nobody
                      has approved. You’ll set it up on the Events page, then come back and pick it.
                    </span>
                  </Link>
                </li>
              </ul>
            </Popover.Content>
          </Popover>
        )}
      </div>

      {scheduled ? (
        <ScheduleEditor value={schedule ?? DEFAULT_SCHEDULE} onChange={onScheduleChange} />
      ) : chosen ? (
        <>
          {chosen.hint && <p className="text-xs text-ink-subtle">{chosen.hint}</p>}
          {chosen.fields.length > 0 ? (
            <p className="flex flex-wrap items-center gap-1.5 text-xs text-ink-subtle">
              <span>Values it brings:</span>
              {chosen.fields.map((f) => (
                <span
                  key={f.name}
                  title={f.values ? `one of ${f.values.join(', ')}` : f.kind}
                  className="rounded border border-info-border bg-info-soft px-1.5 py-0.5 font-medium text-info"
                >
                  {f.label || humanizeKey(f.name)}
                </span>
              ))}
            </p>
          ) : (
            <p className="text-xs text-ink-subtle">
              No fields declared yet — the variable picker fills in once its payload is confirmed on
              the Events page.
            </p>
          )}
        </>
      ) : (
        value !== '' && (
          <p className="text-xs text-warning">
            <span className="font-mono">{value}</span> isn’t in the catalog for you. It may have
            been unregistered, or it may need scopes you don’t hold.{' '}
            <Link // The Events page lives at /events (NOT under /settings) and reads
              // ?tab= from the URL — a wrong prefix here sent people to a
              // blank page at the exact moment they were promised help.
              to="/events?tab=catalog"
              className="underline"
            >
              Check the Events page
            </Link>
            .
          </p>
        )
      )}
    </div>
  );
};
